import { Alert, AlertDescription, Badge } from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';

export const NO_REMITTANCES_LABEL = 'No remittances';

// Amber, like the notice, so tagged rows stand out at a glance.
const NO_REMITTANCES_BADGE_CLASS =
  'whitespace-nowrap border-amber-300 bg-amber-50 text-xs text-amber-900';

const NO_REMITTANCES_TITLE =
  'Roger is not enrolled to receive remittances for this insurance.';

/**
 * Marks a NOA or claim row whose insurance sends Roger no
 * remittances, so the biller checks the other clearinghouse for it.
 */
export function NoRemittancesBadge() {
  return (
    <Badge
      variant='outline'
      className={NO_REMITTANCES_BADGE_CLASS}
      title={NO_REMITTANCES_TITLE}
      data-testid='no-remittances-badge'
    >
      {NO_REMITTANCES_LABEL}
    </Badge>
  );
}

interface NoRemittancesNoticeProps {
  rows: ReadonlyArray<{ receives_remittances?: boolean }>;
  /** What will not arrive on its own for these rows. */
  missing?: string;
  className?: string;
}

/** Explains the badge above a table that shows at least one tagged row. */
export function NoRemittancesNotice({
  rows,
  missing = 'Remittances, payments and denials',
  className,
}: NoRemittancesNoticeProps) {
  if (!rows.some(row => row.receives_remittances === false)) return null;
  // Same amber layout as the claim worksheet notices: the icon sits inline.
  return (
    <Alert
      className={`flex items-start gap-2 border-amber-200 bg-amber-50 p-3 [&>svg+div]:translate-y-0 [&>svg]:static [&>svg]:left-auto [&>svg]:top-auto [&>svg~*]:pl-0 ${className ?? ''}`}
      data-testid='no-remittances-notice'
    >
      <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-600' />
      <div className='min-w-0 space-y-1'>
        <p className='text-sm font-medium text-amber-900'>
          Check your other clearinghouse
        </p>
        <AlertDescription className='text-amber-800'>
          Roger is not enrolled to receive remittances for insurances tagged{' '}
          <Badge variant='outline' className={NO_REMITTANCES_BADGE_CLASS}>
            {NO_REMITTANCES_LABEL}
          </Badge>
          . {missing} for them will not appear here on their own. Check them in
          your other clearinghouse and update their statuses in Roger.
        </AlertDescription>
      </div>
    </Alert>
  );
}
